import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Drawer, Form, InputNumber } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'translations/hooks';
import { type Ring } from 'technical/db';
import { useNbSegmentsByRing } from 'business/tunnel/hooks/use-segments-per-ring';

interface Props {
  rings: Ring[];
  lastRealRingNumber: number;
  selectedRingId: string | null;
  onRingChange: (ringId: string) => void;
}

export const RingSelector = ({
  rings,
  lastRealRingNumber,
  selectedRingId,
  onRingChange,
}: Props) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputNumber, setInputNumber] = useState<number | null>(null);

  const currentIndex = useMemo(
    () => rings.findIndex((r) => r.id === selectedRingId),
    [rings, selectedRingId],
  );

  const selectedRing = rings[currentIndex];
  const minRingNumber = rings[0]?.number;
  const maxRingNumber = rings[rings.length - 1]?.number;

  const goToPrev = () => {
    if (currentIndex > 0) {
      onRingChange(rings[currentIndex - 1].id);
    }
  };

  const goToNext = () => {
    if (currentIndex < rings.length - 1) {
      onRingChange(rings[currentIndex + 1].id);
    }
  };

  const handleDrawerOpen = () => {
    setInputNumber(selectedRing?.number ?? null);
    setDrawerOpen(true);
  };

  const handleConfirm = () => {
    if (inputNumber !== null) {
      const ring = rings.find((r) => r.number === inputNumber);
      if (ring) {
        onRingChange(ring.id);
      }
    }
    setDrawerOpen(false);
  };

  const { maxSegmentsByRing, currentRingInstalledSegments } =
    useNbSegmentsByRing(rings[currentIndex]);

  const isRingCompleted = !!(
    currentRingInstalledSegments &&
    maxSegmentsByRing &&
    currentRingInstalledSegments >= maxSegmentsByRing
  );

  const getRingButtonTitle = () => {
    if (!selectedRing) {
      return t('tunnel.ringSelector.noRing');
    }

    const diff = selectedRing.number - lastRealRingNumber;
    let customClass = '';

    if (diff == 0) {
      customClass = 'font-bold';
    } else if (diff > 0) {
      customClass = 'text-gray-400 font-normal';
    } else {
      customClass = 'font-normal';
    }

    const title = t('tunnel.ringSelector.ring', {
      number: selectedRing.number,
    });

    return (
      <span className={customClass}>
        {title} {isRingCompleted && '✅'}
      </span>
    );
  };

  return (
    <>
      <div className="flex h-14 w-full items-center rounded-md bg-white">
        <Button
          type="text"
          icon={<LeftOutlined />}
          onClick={goToPrev}
          disabled={currentIndex <= 0}
          className="size-14 shrink-0"
        />
        <Button
          type="text"
          onClick={handleDrawerOpen}
          className="text-primary h-full flex-1 text-base font-semibold"
        >
          {getRingButtonTitle()}
        </Button>
        <Button
          type="text"
          icon={<RightOutlined />}
          onClick={goToNext}
          disabled={currentIndex >= rings.length - 1}
          className="size-14 shrink-0"
        />
      </div>
      <Drawer
        placement="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Form
          layout="vertical"
          className="flex flex-col gap-4"
          onFinish={handleConfirm}
        >
          <Form.Item
            label={t('tunnel.ringSelector.inputLabel')}
            className="mb-0"
          >
            <InputNumber
              className="w-full text-base"
              value={inputNumber}
              onChange={setInputNumber}
              min={minRingNumber}
              max={maxRingNumber}
            />
          </Form.Item>
          <Button htmlType="submit" type="primary" className="h-11 w-full">
            {t('common.validate')}
          </Button>
        </Form>
      </Drawer>
    </>
  );
};
